import { ExportOutlined, MoreOutlined, SmileOutlined } from '@ant-design/icons'
import Picker from '@emoji-mart/react'
import { categories, EditChannelModal, useChannels } from '@entities/channel'
import { EmployeeChannelRole, InternetRecourceType } from '@entities/channel/model/types'
import { usePosts } from '@entities/post'
import { SYSTEM_POST_TYPES } from '@shared/lib/constants/constants'
import { Checkbox } from '@shared/ui/checkbox'
import { CustomTag } from '@shared/ui/custom-tag/custom-tag'
import type { TabsProps } from 'antd'
import {
    Avatar,
    Button,
    Card,
    Col,
    ColorPicker,
    Flex,
    Input,
    Popover,
    Row,
    Space,
    Switch,
    Tabs,
    theme,
    Typography
} from 'antd'
import type { AggregationColor } from 'antd/es/color-picker/color'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { EmployeesTab } from './employees-tab'
import { Ord } from './ord-tab'
import './styled.scss'

const { Title, Text } = Typography

export const SettingsPage = () => {
    const {
        token: {
            magenta,
            colorWhite,
            red,
            volcano,
            orange,
            gold,
            lime,
            green,
            cyan,
            blue,
            geekblue,
            purple,
            colorTextDescription
        }
    } = theme.useToken()

    const customColors = useMemo(
        () => [magenta, red, volcano, orange, gold, lime, green, cyan, blue, geekblue, purple],
        [blue, cyan, geekblue, gold, green, lime, magenta, orange, purple, red, volcano]
    )

    const {
        actions: { editChannel, getEmployees, updateInfo },
        channelDetails
    } = useChannels()

    const {
        actions: { createType, deleteType }
    } = usePosts({ channelId: channelDetails?.id })

    const [signature, setSignature] = useState('')
    const [checkedSignature, setCheckedSignature] = useState(false)

    const [newPostType, setNewPostType] = useState('')
    const [newPostTypeColor, setNewPostTypeColor] = useState(customColors[0])

    const [isSilentMode, setIsSilentMode] = useState(false)

    const [isEditChannelModalOpen, setIsEditChannelModalOpen] = useState(false)

    useEffect(() => {
        if (channelDetails?.settings) {
            const {
                general: { signature, silenceMode, showSignature }
            } = channelDetails.settings
            setIsSilentMode(!!silenceMode)
            if (signature) {
                setSignature(signature)
                setCheckedSignature(showSignature)
            }
        }
    }, [channelDetails])

    const toggleSilentMode = useCallback(() => {
        if (channelDetails?.id) {
            editChannel({
                id: channelDetails.id,
                data: {
                    settings: { general: { silenceMode: !isSilentMode } }
                }
            })
        }
        setIsSilentMode(!isSilentMode)
    }, [channelDetails?.id, editChannel, isSilentMode])

    const handleColorChange = (color: AggregationColor) => {
        setNewPostTypeColor(color.toHexString())
    }

    const handleGetEmployees = useCallback(() => {
        if (channelDetails?.id) {
            getEmployees({ id: channelDetails.id })
        }
    }, [channelDetails?.id, getEmployees])

    const handleUpdateInfo = useCallback(() => {
        if (channelDetails?.id) {
            updateInfo({ id: channelDetails.id })
        }
    }, [channelDetails?.id, updateInfo])

    const [showEmojiPicker, setShowEmojiPicker] = useState(false)
    const handleEmojiSelect = (emoji: any) => {
        setNewPostType((prev) => prev + emoji.native)
        setShowEmojiPicker(false)
    }

    // Обработчик добавления пользовательского типа поста
    const handleAddPostType = useCallback(async () => {
        if (channelDetails?.id) {
            await createType({
                color: newPostTypeColor,
                name: newPostType,
                channel: channelDetails?.id
            })
            setNewPostType('')
        }
    }, [channelDetails?.id, createType, newPostType, newPostTypeColor])

    const handleAddSignature = useCallback(() => {
        if (channelDetails?.id) {
            if (!checkedSignature) {
                editChannel({
                    id: channelDetails.id,
                    data: {
                        settings: { general: { signature: signature, showSignature: true } }
                    }
                })
            } else {
                editChannel({
                    id: channelDetails.id,
                    data: {
                        settings: { general: { showSignature: false } }
                    }
                })
            }
        }
        setCheckedSignature(!checkedSignature)
    }, [channelDetails?.id, checkedSignature, editChannel, signature])

    const isChannel = useMemo(() => channelDetails?.type === InternetRecourceType.channel, [channelDetails?.type])

    const ComponentRender = useMemo(() => {
        if (!isChannel) return null

        return (
            <Space direction="vertical" style={{ width: '100%', paddingRight: 100 }} size={16}>
                {/* Пункт "Режим тишины" */}
                <Text strong>Настройка постов:</Text>

                <Flex gap={16} align="center" style={{ marginBottom: 16 }}>
                    <Text strong>Режим тишины</Text>
                    <Switch checked={isSilentMode} onClick={toggleSilentMode} />
                </Flex>

                {/* Подпись */}

                <Flex vertical gap={8} style={{ marginBottom: 16 }}>
                    <Text strong>Подпись под постом</Text>
                    <Row align={'middle'} gutter={16}>
                        <Col span={4}>
                            <Input value={signature} onChange={(e) => setSignature(e.target.value)} />
                        </Col>
                        <Col span={20}>
                            <Checkbox checked={checkedSignature} onChange={handleAddSignature}>
                                Включить подпись
                            </Checkbox>
                        </Col>
                    </Row>
                </Flex>

                {/* Типы постов */}
                <Flex vertical gap={16} style={{ marginBottom: 16 }}>
                    <Text strong>Типы постов:</Text>
                    <Text>Системные</Text>
                    {/* Дефолтные */}
                    <Space size={[0, 8]} wrap>
                        <Flex gap={12}>
                            {SYSTEM_POST_TYPES.map((type) => (
                                <CustomTag color={type.color} key={type.id}>
                                    {type.name}
                                </CustomTag>
                            ))}
                        </Flex>
                    </Space>
                    <Text>Пользовательские</Text>
                    {/* Пользовательские */}
                    <Space size={[0, 8]} wrap>
                        <Flex gap={12}>
                            {channelDetails?.settings?.general.postTypes.map(({ name, color, id }) => (
                                <CustomTag
                                    key={id}
                                    color={color}
                                    onClose={() => {
                                        deleteType(id!)
                                    }}
                                >
                                    {name}
                                </CustomTag>
                            ))}
                        </Flex>
                    </Space>

                    {/* Создаем типы, нужно апи? */}
                    <Flex gap={8}>
                        <Space.Compact>
                            <Input
                                placeholder="Новый тип поста"
                                value={newPostType}
                                onChange={(e) => setNewPostType(e.target.value)}
                                // style={{ width: '33%' }}
                            />
                            <Popover content={<Picker onEmojiSelect={handleEmojiSelect} />}>
                                <Button icon={<SmileOutlined />} onClick={() => setShowEmojiPicker(!showEmojiPicker)} />
                            </Popover>
                        </Space.Compact>

                        <ColorPicker
                            presets={[
                                {
                                    label: 'Пользовательские цвета',
                                    colors: customColors
                                }
                            ]}
                            value={newPostTypeColor}
                            onChange={handleColorChange}
                            disabledAlpha
                        />
                        <Button type="primary" onClick={handleAddPostType} disabled={!newPostType}>
                            Добавить
                        </Button>
                    </Flex>
                </Flex>
                <Flex gap={16} align={'center'}>
                    <Flex vertical gap={8}>
                        <Text strong>Тип ссылки:</Text>
                        <Text style={{ color: colorTextDescription }}>
                            Параметр определяет тип создаваемой ссылки для закупов канала. Учет статистики недоступен
                            для ссылок с заявками в публичный канал.
                        </Text>
                    </Flex>
                </Flex>
                <Flex gap={16} align={'center'} justify={'space-between'}>
                    <Flex vertical gap={8}>
                        <Text strong>Обновить данные канала:</Text>
                        <Text style={{ color: colorTextDescription }}>
                            Займет до 10 минут и обновит название, количество подписчиков и открытую ссылку(если
                            требуется)
                        </Text>
                    </Flex>
                    <Button style={{ padding: '20px', width: 120 }} onClick={handleUpdateInfo}>
                        Обновить
                    </Button>
                </Flex>
                <Flex gap={16} align={'center'} justify={'space-between'}>
                    <Flex vertical gap={8}>
                        <Text strong>Выгрузить подписчиков:</Text>
                        <Text style={{ color: colorTextDescription }}>
                            Выгрузка информации о подписчиках в виде Exel-таблицы. Это может занять 10 минут.
                        </Text>
                    </Flex>
                    <Button style={{ padding: '20px', width: 120 }} onClick={handleGetEmployees}>
                        Скачать
                    </Button>
                </Flex>
            </Space>
        )
    }, [
        channelDetails?.settings?.general.postTypes,
        checkedSignature,
        colorTextDescription,
        customColors,
        deleteType,
        handleAddPostType,
        handleAddSignature,
        handleGetEmployees,
        handleUpdateInfo,
        isChannel,
        isSilentMode,
        newPostType,
        newPostTypeColor,
        showEmojiPicker,
        signature,
        toggleSilentMode
    ])

    const items: TabsProps['items'] = [
        {
            key: '1',
            label: <span style={{ fontSize: '17px' }}>Настройки</span>,
            children: <div style={{ overflow: 'auto' }}>{ComponentRender}</div>
        },
        {
            key: '2',
            label: <span style={{ fontSize: '17px' }}>Сотрудники</span>,
            disabled: channelDetails?.role !== EmployeeChannelRole.owner,
            children: (
                <div style={{ overflow: 'auto' }}>
                    <EmployeesTab />
                </div>
            )
        },
        // {
        //     key: '3',
        //     label: <span style={{ fontSize: '17px' }}>Финансы</span>,
        //     disabled: channelDetails?.role !== EmployeeChannelRole.owner,
        //     children: 'В разработке'
        // },
        {
            key: '4',
            label: <span style={{ fontSize: '17px' }}>ОРД</span>,
            disabled: channelDetails?.role !== EmployeeChannelRole.owner,
            children: (
                <div style={{ overflow: 'auto' }}>
                    <Ord />
                </div>
            )
        }
        // {
        //     key: '5',
        //     label: <span style={{ fontSize: '17px' }}>Подписка</span>,
        //     disabled: channelDetails?.role !== EmployeeChannelRole.owner,
        //     children: (
        //         <div style={{ maxHeight: !isMobile ? 'calc(100vh - 540px)' : undefined, overflow: 'auto' }}>
        //             <SubscriptionTab />
        //         </div>
        //     )
        // }
    ]

    return (
        <Card>
            {/* Заголовок страницы */}
            <Flex justify="space-between" align="center" style={{ marginBottom: '16px' }}>
                <Title level={3}>Настройки</Title>
            </Flex>

            {/* Данные канала */}
            <Space size={20} align="start">
                <Avatar
                    shape="circle"
                    size={100}
                    src={isChannel ? channelDetails?.photoUrl : `${channelDetails?.url}/favicon.ico`}
                />
                <Flex vertical={true} gap={6}>
                    <Title
                        level={4}
                        style={{ margin: 0, cursor: 'pointer' }}
                        onClick={() =>
                            isChannel
                                ? window.open(`https://t.me/${channelDetails?.username}`)
                                : window.open(`${channelDetails?.url}`)
                        }
                    >
                        <Space>
                            {channelDetails?.name}
                            <ExportOutlined />
                        </Space>
                    </Title>

                    {isChannel ? (
                        <>
                            <Text italic>@{channelDetails?.username}</Text>
                            <Text>Правообладатель: @{channelDetails?.owner}</Text>
                            <Space size={2}>
                                <Text>{channelDetails?.timezone}</Text>
                                <Button
                                    type="text"
                                    style={{ padding: '4px' }}
                                    onClick={() => setIsEditChannelModalOpen(true)}
                                >
                                    <MoreOutlined />
                                </Button>
                            </Space>
                            <Text>{channelDetails?.membersCount} подписчиков</Text>
                            <Text>{categories.find((c) => c.value === channelDetails?.category)?.label}</Text>
                        </>
                    ) : (
                        <></>
                    )}
                </Flex>
            </Space>

            {/* Меню закладок */}
            <Tabs defaultActiveKey="1" items={items} onChange={() => {}} />

            {channelDetails && isEditChannelModalOpen && (
                <EditChannelModal
                    channel={channelDetails}
                    open={isEditChannelModalOpen}
                    onClose={() => setIsEditChannelModalOpen(false)}
                />
            )}
        </Card>
    )
}
